import { useEffect, useState } from "react";

import { useTranslation } from "react-i18next";

import { useVehiclesTableStore } from "@/stores/useTableStore";

import useAxiosPrivate from "../../../../../../../api/hooks/useAxiosPrivate";
import { ApiResources } from "../../../../../../../api/resources";
import { ALERT_STATUS } from "../../../../../../../context/alert/types";
import { VehicleDataTypes, useMediaQueries } from "../../../../../../../shared";
import DialogActions from "../../../../../../../shared/components/buttons/DialogActions";
import { refreshTableAndUpdateDetails } from "../../../../../../../shared/functions/functions";
import useResponse from "../../../../../../../shared/hooks/useResponse";
import Dialog from "../../../../../../Dialog/Dialog";
import { DialogWidth } from "../../../../../../Dialog/types/enums";
import useTableItemDetails from "../../../../../hooks/useTableItemDetails";
import { DRIVER_SCOPE, QuickDownloadPayloadDto } from "../types";

import VehicleQuickDownloadScheduleDialogContent from "./VehicleQuickDownloadScheduleDialogContent";

interface VehicleQuickDownloadScheduleDialogProps {
  vehicleId: string | string[];
  isOpen: boolean;
  handleClose: () => void;
  isMultipleIds?: boolean;
}

const VehicleQuickDownloadScheduleDialog = ({
  vehicleId,
  isOpen,
  handleClose,
  isMultipleIds
}: VehicleQuickDownloadScheduleDialogProps) => {
  const { toMd } = useMediaQueries();
  const { t } = useTranslation();

  const { postData } = useAxiosPrivate();
  const { getTableItemDetails } = useTableItemDetails();
  const { handleCommonError, handleResponse } = useResponse();

  const params = useVehiclesTableStore(state => state.params);
  const detailsData = useVehiclesTableStore(state => state.detailsData);

  const [quickDownloadPayload, setQuickDownloadPayload] =
    useState<QuickDownloadPayloadDto>({
      vehicleId: vehicleId,
      fileTypes: [VehicleDataTypes.Overview],
      activities: null,
      driverScope: DRIVER_SCOPE.None
    });

  const [inputError, setInputError] = useState("");

  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    setQuickDownloadPayload(prev => ({
      ...prev,
      vehicleId: vehicleId
    }));
  }, [vehicleId]);

  const handleSubmit = async () => {
    try {
      setIsLoading(true);

      const resource = isMultipleIds
        ? ApiResources.VehicleSchedulesQuickDownloadBulk
        : ApiResources.VehicleSchedulesQuickDownload;

      const { vehicleId, fileTypes, activities, driverScope } =
        quickDownloadPayload;

      const finalPayload = isMultipleIds
        ? {
            vehicleIds: vehicleId,
            fileTypes,
            activities,
            driverScope
          }
        : quickDownloadPayload;

      const { data } = await postData(resource, finalPayload);

      handleContentRefresh();

      if (isMultipleIds) {
        const dataItems = data?.items;

        const count = dataItems.filter(
          (item: any) => !item.failureReason
        ).length;

        const alertTitle = t(
          "Alert##quick schedule created##title multiple vehicles",
          {
            vehicleNum: count
          }
        );

        handleResponse(
          ALERT_STATUS.Info,
          alertTitle,
          t("Alert##quick schedule created##description##vehicles")
        );
      } else {
        handleResponse(
          ALERT_STATUS.Info,
          t("Alert##quick schedule created##title"),
          t("Alert##quick schedule created##description##vehicles")
        );
      }

      handleClose();
    } catch (error) {
      handleCommonError(error, t);
    }

    setIsLoading(false);
  };

  const handleContentRefresh = () => {
    const valueInSearchField = params.contains;

    if (!isMultipleIds) {
      return refreshTableAndUpdateDetails({
        valueInSearchField,
        resourceId: vehicleId,
        itemHasOpenedDetails: true,
        getTableItemDetails,
        handleResponse
      });
    }

    const hasDetails = detailsData && Array.isArray(vehicleId);

    const itemHasOpenedDetails =
      hasDetails && vehicleId.includes(detailsData.id);

    const resourceId = hasDetails
      ? vehicleId.find(id => id === detailsData.id)
      : undefined;

    return refreshTableAndUpdateDetails({
      valueInSearchField,
      resourceId,
      itemHasOpenedDetails,
      getTableItemDetails,
      handleResponse
    });
  };

  const resetValueStates = () => {
    setQuickDownloadPayload({
      ...quickDownloadPayload,
      vehicleId: vehicleId,
      fileTypes: [VehicleDataTypes.Overview],
      activities: null,
      driverScope: DRIVER_SCOPE.None
    });

    setInputError("");
  };

  return (
    <Dialog
      data-testid="vehicle-quick-schedule-dialog"
      title={t("Schedules##quick schedule##title")}
      description={t("Schedules##quick schedule##description")}
      open={isOpen}
      isTitleSeparator
      isActionsSeparator
      extendToMaxWidth={toMd}
      width={toMd ? undefined : DialogWidth.MinWidth}
      close={handleClose}
      submit={handleSubmit}
      slotProps={{
        transition: { onExited: () => resetValueStates() }
      }}
      actions={
        <DialogActions
          onClose={handleClose}
          isLoading={isLoading}
          isreadyToConfirm={inputError === ""}
          confirmationButtonText={t("Button##save")}
          confirmationButtonTestId="vehicle-quick-schedule-save-button"
          cancelButtonText={t("Button##close")}
          cancelButtonTestId="vehicle-quick-schedule-close-button"
        />
      }
    >
      <VehicleQuickDownloadScheduleDialogContent
        quickDownloadPayload={quickDownloadPayload}
        setInputError={setInputError}
        setQuickDownloadPayload={setQuickDownloadPayload}
      />
    </Dialog>
  );
};

export default VehicleQuickDownloadScheduleDialog;
